import React, { ReactNode, useContext, useMemo } from "react";
import { useTheme } from "styled-components";
import { Clock3 } from "lucide-react";
import {
  Direction,
  LanguageNameSpaces,
  OrderStatus,
  SignalProcessedGeneralData,
} from "@shared/types";
import { SignalDetailsContext } from "@shared/features/signals/common/SignalDetails.context";
import { EditTargetOrZone } from "@shared/features/signals/EditSignal/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import { formatDate } from "@shared/utils/date";
import { round } from "@shared/utils/numbers";
import PriceLadder from "common/NewLook/PriceLadder.tsx";
import {
  LadderKind,
  LadderLevel,
  LevelsLadderModel,
} from "common/NewLook/priceLadder.ts";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { directionColor, getNewLookTokens } from "common/NewLook/tokens.ts";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup.tsx";
import {
  DetailsEmpty,
  DetailsField,
  DetailsFields,
  DetailsLabel,
  DetailsSection,
  DetailsTitle,
  DetailsValue,
  DetailsValueText,
  PROGRESS_LADDER_STYLE,
  ProgressCaption,
  ProgressFigures,
  ProgressHead,
  ProgressLabel,
  ProgressProfit,
  ProgressTime,
  ProgressWell,
} from "features/trades/NewLook/drawerDetails.ts";
import { useLadderCaption } from "features/trades/NewLook/useLadderCaption.ts";
import { formatSignedPercent } from "features/trades/NewLook/useMoneyFormat.ts";
import {
  formatTimeInTrade,
  useLadderPriceFormat,
} from "features/trades/NewLook/useTradeLadder.ts";
import { translateOptionName } from "@shared/common/i18n/optionNames";

/**
 * New look of the signal drawer's General tab, the same as the trade
 * drawer's: the signal's progress (PnL %, time, and the price ladder of its
 * targets) leads, then the details as hairline-separated groups.
 */
const NewLookSignalGeneral = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { t: tTrades } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const tokens = getNewLookTokens(useTheme());
  const { generalDataQuery, targets, currentPrice, symbol, selectedExchange } =
    useContext(SignalDetailsContext)!;
  const data = generalDataQuery.data;
  const general = data?.general;
  const graphData = data?.graph_data;
  const isOpen = !!data?.is_open;
  const formatPrice = useLadderPriceFormat(
    symbol ?? data?.symbol,
    selectedExchange
  );

  const model = useMemo((): LevelsLadderModel | undefined => {
    if (!targets) return undefined;
    const levels: LadderLevel[] = [];
    const add = (kind: LadderKind, list: EditTargetOrZone[]) =>
      list.forEach((target, index) => {
        const raw = Array.isArray(target.target_price)
          ? target.target_price[0]
          : target.target_price;
        const price = parseFloat(String(raw));
        if (!Number.isFinite(price)) return;
        const status = String(target.status ?? "");
        if (status.startsWith(OrderStatus.CANCELLED)) return;
        levels.push({
          kind,
          price,
          filled: status.startsWith(OrderStatus.FULFILLED) ? 1 : 0,
          number: index + 1,
          status,
        });
      });
    add("entry", targets.entryTargets);
    add("take_profit", targets.takeProfitTargets);
    add("stop", targets.stopTargets);
    if (!levels.length) return undefined;
    return {
      type: "levels",
      isShort: general?.["Long/Short"] === Direction.SHORT,
      levels,
      isComplete: true,
      currentPrice: isOpen ? currentPrice : undefined,
    };
  }, [targets, general, currentPrice, isOpen]);

  if (!data) return <SignalGeneralSkeleton />;

  const profit =
    graphData?.profit !== undefined ? round(graphData.profit, 2) : undefined;

  const getValue = (key: string, value: unknown): ReactNode => {
    if (value === null || value === undefined || value === "")
      return <DetailsEmpty>{t("table.na")}</DetailsEmpty>;
    if (key === "Exchanges")
      return (
        <ExchangesIconsGroup exchanges={String(value).split(", ")} size={24} />
      );
    if (DATE_KEYS.includes(key)) return formatDate(value as number, true, true);
    if (key === "Long/Short")
      return (
        <span
          style={{
            color: value === Direction.SHORT ? tokens.down : tokens.up,
          }}
        >
          {translateOptionName(String(value))}
        </span>
      );
    if (key === "Margin Type") return translateOptionName(String(value));
    return String(value);
  };

  return (
    <>
      <ProgressWell>
        <ProgressHead>
          <ProgressLabel>{tTrades("table.headers.progress")}</ProgressLabel>
          {graphData?.time_passed && (
            <ProgressTime>
              <Clock3 size={12} strokeWidth={2} />
              {formatTimeInTrade(graphData.time_passed)}
            </ProgressTime>
          )}
        </ProgressHead>
        <ProgressFigures>
          {profit !== undefined && (
            <ProgressProfit style={{ color: directionColor(tokens, profit) }}>
              {formatSignedPercent(profit)}
            </ProgressProfit>
          )}
          {model && <LadderCaption model={model} formatPrice={formatPrice} />}
        </ProgressFigures>
        {model && (
          <PriceLadder
            model={model}
            size="large"
            formatPrice={formatPrice}
            background={tokens.well}
            style={PROGRESS_LADDER_STYLE}
          />
        )}
      </ProgressWell>
      {SECTIONS.map(({ title, keys }) => {
        const rows = keys.filter((key) => general && key in general);
        if (!rows.length) return null;
        return (
          <DetailsSection key={title}>
            <DetailsTitle>{t(title)}</DetailsTitle>
            <DetailsFields>
              {rows.map((key) => (
                <DetailsField key={key}>
                  <DetailsLabel>
                    {t(FIELD_NAME_TO_TRANSLATION_KEY[key])}
                  </DetailsLabel>
                  <DetailsValue>
                    <DetailsValueText>
                      {getValue(
                        key,
                        general?.[key as keyof SignalProcessedGeneralData]
                      )}
                    </DetailsValueText>
                  </DetailsValue>
                </DetailsField>
              ))}
            </DetailsFields>
          </DetailsSection>
        );
      })}
    </>
  );
};

export default NewLookSignalGeneral;

const LadderCaption = ({
  model,
  formatPrice,
}: {
  model: LevelsLadderModel;
  formatPrice: (price: number) => string;
}) => {
  const caption = useLadderCaption(model, formatPrice);
  return (
    <ProgressCaption $isWarning={caption.isWarning}>
      {caption.text}
    </ProgressCaption>
  );
};

const SignalGeneralSkeleton = () => (
  <div aria-busy={true}>
    <Skeleton width="100%" height={148} radius={12} />
    {[3, 6].map((count, sectionIndex) => (
      <DetailsSection key={sectionIndex}>
        <Skeleton width={120} height={14} style={{ marginBottom: 16 }} />
        <DetailsFields>
          {Array.from({ length: count }, (_, index) => (
            <DetailsField key={index}>
              <Skeleton width={72} height={10} />
              <Skeleton width={112} height={14} />
            </DetailsField>
          ))}
        </DetailsFields>
      </DetailsSection>
    ))}
  </div>
);

const DATE_KEYS = ["Opened at", "Closed at"];

const SECTIONS: {
  title: TranslationKeys<LanguageNameSpaces.SIGNALS>;
  keys: string[];
}[] = [
  {
    title: "general_info.group_and_exchanges",
    keys: ["Group", "Exchanges", "Published by"],
  },
  {
    title: "general_info.signal_details",
    keys: [
      "Opened at",
      "Long/Short",
      "Leverage",
      "Regular/Breakout",
      "Number of open trades",
      "Number of trades",
      "Open Trades Volume",
    ],
  },
];

const FIELD_NAME_TO_TRANSLATION_KEY: Record<
  string,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  Group: "general_info.group",
  Exchanges: "general_info.exchanges",
  "Published by": "general_info.published_by",
  "Opened at": "general_info.opened_at",
  "Long/Short": "general_info.long_short",
  Leverage: "general_info.leverage",
  "Regular/Breakout": "general_info.regular_breakout",
  "Number of open trades": "general_info.number_of_open_trades",
  "Number of trades": "general_info.number_of_trades",
  "Open Trades Volume": "general_info.open_trades_volume",
};
